import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fmtNum } from "@/lib/format";
import { Search, Plus, X, Repeat } from "lucide-react";

export const AlertCreateCard = ({ t, onCreated }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [alertType, setAlertType] = useState("price");
  const [target, setTarget] = useState("");
  const [percent, setPercent] = useState("5");
  const [direction, setDirection] = useState("above");
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const search = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/market/instruments`, { params: { search: q } });
        setResults((data || []).slice(0, 8));
      } catch (e) { setResults([]); }
    }, 250);
  };

  const pick = (inst) => {
    setSelected(inst);
    setResults([]);
    setQuery(`${inst.symbol} — ${inst.name}`);
    // sensible default target = 5% away in the chosen direction
    const p = inst.price;
    setTarget(direction === "above" ? fmtNum(p * 1.05) : fmtNum(p * 0.95));
  };

  const clearSel = () => { setSelected(null); setQuery(""); setTarget(""); setResults([]); };

  const create = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const body = { symbol: selected.symbol, alert_type: alertType, direction, recurring };
      if (alertType === "price") {
        if (!target) { setBusy(false); return; }
        body.target_price = Number(target);
      } else {
        if (!percent) { setBusy(false); return; }
        body.percent = Number(percent);
      }
      await api.post("/alerts", body);
      toast.success(t("alr_create"), { description: `${selected.symbol} · ${t(`alr_type_${alertType}`)}` });
      clearSel();
      await onCreated();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 shadow-premium lg:col-span-1 h-fit" data-testid="alert-create-card">
      <div className="flex items-center gap-2 text-sm font-medium mb-4"><Plus className="h-4 w-4" />{t("alr_create")}</div>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("alr_search")}</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="ps-9 pe-9" value={query} onChange={(e) => search(e.target.value)}
              placeholder="AAPL, BTC, Gold..." data-testid="alert-search-input" />
            {(query || selected) && (
              <button onClick={clearSel} className="absolute end-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="alert-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {results.length > 0 && (
            <div className="mt-1 rounded-lg border bg-popover shadow-md overflow-hidden" data-testid="alert-search-results">
              {results.map((r, i) => (
                <button key={`${r.symbol}-${i}`} onClick={() => pick(r)}
                  className="w-full text-start px-3 py-2 hover:bg-accent transition-colors flex items-center justify-between gap-2"
                  data-testid={`alert-result-${r.symbol}`}>
                  <span className="min-w-0">
                    <span className="font-medium text-sm">{r.symbol}</span>
                    <span className="text-xs text-muted-foreground ms-2 truncate">{r.name}</span>
                  </span>
                  <span className="numeric text-xs text-muted-foreground">{fmtNum(r.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div className="rounded-lg bg-secondary/60 p-3 flex items-center justify-between" data-testid="alert-selected">
            <div>
              <div className="font-medium text-sm">{selected.symbol}</div>
              <div className="text-xs text-muted-foreground truncate max-w-[160px]">{selected.name}</div>
            </div>
            <div className="text-end">
              <div className="text-[11px] text-muted-foreground">{t("alr_current")}</div>
              <div className="numeric font-semibold">{fmtNum(selected.price)}</div>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>{t("alr_alert_type")}</Label>
          <Select value={alertType} onValueChange={setAlertType}>
            <SelectTrigger data-testid="alert-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="price">{t("alr_type_price")}</SelectItem>
              <SelectItem value="percent_move">{t("alr_type_percent_move")}</SelectItem>
              <SelectItem value="daily_change">{t("alr_type_daily_change")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>{t("alr_direction")}</Label>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger data-testid="alert-direction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="above">{alertType === "price" ? t("alr_rises_above") : t("alr_up")}</SelectItem>
              <SelectItem value="below">{alertType === "price" ? t("alr_falls_below") : t("alr_down")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {alertType === "price" ? (
          <div className="space-y-1.5">
            <Label>{t("alr_target_price")}</Label>
            <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)}
              disabled={!selected} data-testid="alert-target-input" />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>{t("alr_percent")}</Label>
            <div className="relative">
              <Input type="number" value={percent} onChange={(e) => setPercent(e.target.value)}
                disabled={!selected} className="pe-8" data-testid="alert-percent-input" />
              <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {alertType === "percent_move"
                ? `${direction === "above" ? "+" : "-"}${percent || 0}% ${t("alr_from_current")}${selected ? ` (${fmtNum(selected.price)})` : ""}`
                : `${t("alr_when_daily")} ${direction === "above" ? "+" : "-"}${percent || 0}%`}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg border p-3" data-testid="alert-recurring-row">
          <div className="flex items-center gap-2">
            <Repeat className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Repeat</div>
              <div className="text-[11px] text-muted-foreground">Re-arm after each cross</div>
            </div>
          </div>
          <Switch checked={recurring} onCheckedChange={setRecurring} data-testid="alert-recurring-switch" />
        </div>

        <Button className="w-full" onClick={create} disabled={busy || !selected || (alertType === "price" ? !target : !percent)} data-testid="alert-create-submit">
          {busy ? "..." : t("alr_add")}
        </Button>
      </div>
    </Card>
  );
};
